import Search from './Search';
import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fromJS, is, List, Map } from 'immutable';
import { Sidebar } from 'semantic-ui-react';
import { DataGrid, Toast } from '../../Common';
import Actions from './actions';
import SettingCustomerActions from '../Customer/actions';
import { NUMERIC_FORMAT } from '../../../Constants/Utils';

const Detail = lazy(() => import('./Detail'));
const Statement = lazy(() => import('./Statement'));

export default function SettingRevenue() {
    const dispatch = useDispatch();

    const loading_index = useSelector(
        state => state.SettingRevenueReducer.get('loading_index'),
        (a, b) => is(a, b)
    );

    const loading_show = useSelector(
        state => state.SettingRevenueReducer.get('loading_show'),
        (a, b) => is(a, b)
    );

    const search = useSelector(
        store => store.SettingRevenueReducer.get('search'),
        (a, b) => is(a, b)
    );

    const revenues = useSelector(
        state => state.SettingRevenueReducer.get('revenues'),
        (a, b) => is(a, b)
    );

    const revenue = useSelector(
        state => state.SettingRevenueReducer.get('revenue'),
        (a, b) => is(a, b)
    );

    const deposit = useSelector(
        state => state.SettingRevenueReducer.get('deposit'),
        (a, b) => is(a, b)
    );

    const defaultColumns = useSelector(
        store => store.SettingRevenueReducer.get('columns'),
        (a, b) => is(a, b)
    );

    const columns = useSelector(
        store => store.MainReducer.getIn(['gui', 'Customer'], defaultColumns),
        (a, b) => is(a, b)
    ).toJS();

    useEffect(() => {
        dispatch(SettingCustomerActions.options());
        return () => {
            dispatch(Actions.reset());
        };
    }, []);

    useEffect(() => {
        dispatch(Actions.requests());
    }, [search]);

    const handleSearch = (evt, values) => {
        const { name, value } = values;
        if (name) {
            dispatch(Actions.search(search.set(name, value instanceof Array ? value.join(',') : value)));
        } else {
            dispatch(Actions.search(search.merge(fromJS(values))));
        }
    };

    const handleAction = (evt, { action }) => {
        switch (action) {
            case 'add':
                dispatch(Actions.show());
                return DetailRef?.current?.handleOpen();
            case 'update':
                if (!revenue.get('income') && !revenue.get('outcome')) {
                    return Toast.error('金额不得空白');
                }
                const [TOTAL_OFFSET] = (revenue.get('offsets') ?? List()).reduce(
                    (a, b) => [a[0] + Number(b.get('offsets') ?? 0)],
                    [0]
                );

                if (revenue.get('income') - TOTAL_OFFSET < 0) {
                    return Toast.error('未冲金额不得少於零');
                }

                return dispatch(Actions.create());
            case 'destroy':
                return dispatch(Actions.destroy());
            case 'refresh':
                return dispatch(Actions.requests());
            case 'offset':
                return StatementRef.current.handleOpen();
            default:
        }
    };

    const handleChange = (evt, values) => {
        const { name, value } = values;
        if (!!value) {
            dispatch(Actions.show(revenue.set(name, fromJS(value))));
        } else {
            dispatch(Actions.show(revenue.delete(name)));
        }
    };

    const handleSelect = row => {
        if (row.id) {
            dispatch(Actions.request(row.id));
            return DetailRef?.current?.handleOpen();
        }
    };

    const DetailRef = useRef();
    const StatementRef = useRef();

    const [TOTAL_INCOME, TOTAL_OUTCOME] = revenues.reduce(
        (a, b) => [a[0] + Number(b.get('income') ?? 0), a[1] + Number(b.get('outcome') ?? 0)],
        [0, 0]
    );

    const TOTAL_USEABLE = Number((revenues.last() ?? Map()).get('final')) - deposit;

    const status = [
        { label: '期初结存', value: NUMERIC_FORMAT((revenues.first() ?? Map()).get('final'), 2) },
        { label: '本期收入', value: NUMERIC_FORMAT(TOTAL_INCOME, 2) },
        { label: '本期支出', value: NUMERIC_FORMAT(TOTAL_OUTCOME, 2) },
        { label: '期末结存', value: NUMERIC_FORMAT((revenues.last() ?? Map()).get('final'), 2) },
        { label: '押金合计', value: NUMERIC_FORMAT(deposit, 2) },
        { label: '可用余额', value: NUMERIC_FORMAT(TOTAL_USEABLE, 2) },
    ];

    return (
        <div className="flexHorizontalContent">
            <Sidebar.Pushable className="flexHorizontalContent">
                <Search search={search} onSearch={handleSearch} onAction={handleAction} />
                <DataGrid
                    refresh={false}
                    addition={false}
                    loading={loading_index}
                    columns={columns}
                    rows={revenues}
                    defaultSortColumns={[{ columnKey: 'name', direction: 'ASC' }]}
                    onAction={handleAction}
                    onSelect={handleSelect}
                    status={status}
                />
                <Suspense fallback={null}>
                    <Detail
                        ref={DetailRef}
                        loading={loading_show}
                        revenue={revenue}
                        StatementRef={StatementRef}
                        onAction={handleAction}
                        onChange={handleChange}
                    />
                    <Statement ref={StatementRef} />
                </Suspense>
            </Sidebar.Pushable>
        </div>
    );
}

export { default as SettingRevenueReducer } from './reducers';
export { default as SettingRevenueAction } from './actions';
